import { Injectable, Inject } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ConfigI, RemoteConfigParams, ServerConfig, VirtualAlgoSpaceConfig } from './configuration.interfaces';
import { HttpClient } from '@angular/common/http';
import { tap, catchError, share, finalize } from 'rxjs/operators';
import { DOCUMENT } from '@angular/common';
import { CONFIGURABLE_DETAILS, CONFIGURABLE_IMAGES, DEFAULT_VIRTUAL_ALGO_SPACE_CONFIG, FIREBASE_FETCH_TIMEOUT, MOMENT_HOLIDAYS_FORMAT, SELECTED_VENDOR } from '../../../_helpers/constants';
import { BaseUrls } from '../../../_helpers/urls/base.urls';
import { Observable, of, BehaviorSubject } from 'rxjs';
import * as moment from 'moment';
import { MarketKeywords } from '@app/app.structs';
import { fetchAndActivate, getRemoteConfig, RemoteConfig, getAll } from 'firebase/remote-config';
import { Utils } from '@app/_helpers';
import { initializeApp } from "firebase/app";
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { FirebaseErrorComponent } from '@app/_shared/modules/firebase-error/firebase-error.component';
import { cloneDeep } from 'lodash-es';
import { environment } from '../../../../environments/environment';


@Injectable({
    providedIn: 'root'
})
export class ConfigurationService implements ConfigI {
    serverConfig: ServerConfig;
    serverConfig$: Observable<ServerConfig>;

    remoteConfigCacheObj: RemoteConfigParams = {};


    // URLs for whole apps will be used from here.
    AUTH_HOST: string;
    MARKET_SOCKET_HOST: string;
    REST_SERVER_HOST:string;
    ALGO_SERVER_HOST: string;
    FLASK_SERVER: string;
    FAST_API_SERVER: string;
    OPTION_CALCULATOR_SERVER: string;
    U_GATEWAY: string;
    EKYC_SERVER: string;
    OPERATOR_DASHBOARD_HOST: string;
    BINARY_MARKET_SOCKET = false;

    AIASSISTANT_SERVER_HOST: string;
    AI_RECOMMENDATION_SERVER: string;
    ANALYTICS_SERVER: string;

    applicationUrls: BaseUrls;

    OTP_RESENT_ATTEMPTS = 5;
    OTP_EXPIRE_TIME = 5;
    OTP_MAX_LENGTH = 6;

    // No oAuths are enabled by default. (RA-98)
    OAUTHS_ALLOWED = [];

    //State Codes
    stateCodes = {};

    // hiding Transaction history page for ticket UT-980 | bringing it back as mentioned in UT-1525
    private showTransactionHistoryPage = new BehaviorSubject<boolean>(true);

    constructor(
        @Inject(DOCUMENT) private document: HTMLDocument,
        private http: HttpClient,
        private titleService: Title,
        private dialog: MatDialog
    ) { }


    async loadConfigurationFile() {
        // Setting favicon according to selected vendor
        this.document.getElementById('appFavicon')?.setAttribute('href', CONFIGURABLE_IMAGES.faviconUrl);

        // Set Title of document according to vendor
        this.titleService.setTitle(CONFIGURABLE_DETAILS.appName);
        //State codes file load
        this.http.get('/assets/json/state_codes.json').pipe(catchError((err, _) => {
            console.error('Error in parsing state codes file', err);
            throw (err);
        })).subscribe((res) => this.stateCodes = res);

        const config = await this.http.get('/assets/json/firebase.json').toPromise();
        if (config?.hasOwnProperty('firebase')) {
            try {
                // Initialize Firebase
                const app = initializeApp(config['firebase']);
                const remoteConfig = getRemoteConfig(app);
                remoteConfig.settings.minimumFetchIntervalMillis = FIREBASE_FETCH_TIMEOUT;
                return await this.loadConfig(remoteConfig);
            } catch (err) {
                await this.setBackupParams();
            }
        } else {
            await this.setBackupParams();
        }


    }

    firebasePopup() {
        this.dialog.open(FirebaseErrorComponent, {
            disableClose: true,
            width: '300px',
            panelClass: ['email-communicaion-dialog']
        })
        // commenting for now as we are using firebase for configuration
        // console.log("Using env.json")
        // // Adding timestamp so that this file in not cached.
        // return this.http.get('/assets/json/env.json', {
        //     params: { timestamp: new Date().getTime() } as any
        // })
        //     .pipe(
        //         tap(env => {
        //             if (!env) { return; }
        //             Object.keys(env).forEach(key => {
        //                 this[key] = env[key];
        //             });

        //             this.initBackendTypes()
        //         }),
        //         catchError((err, _) => {
        //             console.error('Error in parsing env file', err);
        //             throw (err);
        //         })
        //     ).toPromise();
    }

    initBackendTypes() {
        // Initalizing Backend Type
        this.applicationUrls = new SELECTED_VENDOR.backendType(
            this.AUTH_HOST,
            this.REST_SERVER_HOST,
            this.ALGO_SERVER_HOST,
            this.FLASK_SERVER,
            this.FAST_API_SERVER,
            this.OPTION_CALCULATOR_SERVER,
            this.U_GATEWAY,
            this.AIASSISTANT_SERVER_HOST,
            this.EKYC_SERVER,
            this.OPERATOR_DASHBOARD_HOST,
            this.AI_RECOMMENDATION_SERVER,
            this.ANALYTICS_SERVER
        );
    }

    handleUrls(obj: any) {
        if (obj) {
            Object.keys(obj).forEach((key) => {
                try {
                    this[key] = eval(obj[key]);
                } catch (err) {
                    this[key] = obj[key];
                }
            })
            this.initBackendTypes();
        }
    }

    async loadConfig(remotConfig: RemoteConfig) {
        try {
            const active = await fetchAndActivate(remotConfig)
            try {
                const values = getAll(remotConfig) as any;
                if (values && Object.keys(values).length) {

                    const transformedObject = Object.keys(values).reduce((acc, key) => {
                        // For each key in the original object, map it to the 'b' property of its inner object
                        acc[key] = values[key]._value;
                        return acc;
                    }, {}); // Initialize the accumulator as an empty object


                    this.handleUrls(transformedObject);

                    // Not used right now, will be used when we need values other that URLs
                    Object.keys(values).forEach((key) => {
                        if (Utils.isJson(values[key]._value)) {
                            this.remoteConfigCacheObj[key] = JSON.parse(values[key]._value);
                        } else {
                            this.remoteConfigCacheObj[key] = values[key]._value;
                        }
                    });
                } else {
                    if (this.remoteConfigCacheObj && Object.keys(this.remoteConfigCacheObj).length) {
                        this.handleUrls(this.remoteConfigCacheObj);
                    } else {
                        await this.setBackupParams();
                    }
                }
                // resolve();
            } catch (err) {
                await this.setBackupParams();
            }

        } catch (err) {
            await this.setBackupParams();
        };
    }


    getServerConfig(refresh = false) {
        if (this.serverConfig && !refresh) {
            return of(this.serverConfig)
        }
        if (!this.serverConfig$ || refresh) {
            this.serverConfig$ = this.http.get<ServerConfig>(this.applicationUrls.serverConfig)
                .pipe(
                    share(),
                    finalize(() => this.serverConfig$ = null),
                    tap((res) => this.serverConfig = res)
                );
        }
        return this.serverConfig$;
    }

    checkForHoliday(holidays, exchange = MarketKeywords.NSEFO) {
        const currentDay = moment().format(MOMENT_HOLIDAYS_FORMAT);
        let isHoliday = false;
        Object.keys(holidays).forEach(holiday => {
            let datePart = holiday.split('_');
            let date = moment(datePart[0], MOMENT_HOLIDAYS_FORMAT).format(MOMENT_HOLIDAYS_FORMAT)
            if (date === currentDay) {
                isHoliday = datePart[1] === exchange;
            }
        });
        return isHoliday;
    }

    get isForwardAllowed() {
        return this.serverConfig?.forward_enabled;
    }

    get isTodayHoliday() {

        if (this.serverConfig) {
            return this.checkForHoliday(this.serverConfig.holidays);

        } else {
            this.getServerConfig().subscribe(conf => {
                return this.checkForHoliday(conf.holidays);
            })
        }
    }

    // hiding Transaction history page for ticket UT-980
    // used in Guard to check if transaction history page is allowed or not
    get isTransactionHistoryPageAllowed() {
        return this.showTransactionHistoryPage.asObservable();
    }

    async setBackupParams() {
        let urls = await this.http.get('/assets/json/env.json').toPromise();
        this.handleUrls(urls);
        this.getServerConfig(true).subscribe((params) => {
            let backupParams = JSON.parse(JSON.parse(params['config_params']['BACKUP_PARAMS']));
            this.remoteConfigCacheObj = cloneDeep(backupParams);  
        });
    }

    get recaptchaRequired() {
        return this.remoteConfigCacheObj?.RECAPTCHA_REQUIRED;
    }

    get recaptchaSitekey() {
        return environment.production ? this.remoteConfigCacheObj?.RECAPTCHA_SITEKEY : '1x00000000000000000000AA';
    }
}
